<template>
  <div class="c-canvas-metadata-filters flex gap-2">
    <div class="flex flex-wrap items-center gap-2">
      <Component
        :is="componentMap[f.componentName]"
        v-for="f in filters"
        :key="f.name"
        :allow-custom-date-range="f.type === 'date'"
        :disabled="disabled"
        :filter-key="f.name"
        :modelValue="metadataFilterValues[f.name]"
        :multiple="f.type === 'multi-select' ? true : undefined"
        :name="f.label || f.name"
        :options="getFilterOptions(f.type, f.options)"
        @update:modelValue="(v: any) => onUpdateModelValue(f, v)"
      />

      <CButton
        v-if="hasActiveFilters"
        class="pa-1 pl-2 !normal-case !tracking-normal underline"
        :disabled="disabled"
        size="tiny"
        :style="{ borderLeft: `1px solid ${themeVars.primary5}` }"
        text
        @click="emit('update:metadata-filter-values', {})"
      >
        {{ t('canvasUI.components.canvases.CCanvasMetadataFilters.clearFilters') }}
      </CButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, onMounted, ref, computed } from 'vue'
import { omitBy } from 'lodash-es'
import { useI18n } from 'vue-i18n'

import type { GetInstanceMetadataTemplatesPayload } from '@sdk/payload.types'
import { type PaginatedMetadataTemplateList, type MetadataTemplateField, MetadataTemplateFieldTypeEnum } from '@openapi'
import CCheckboxFilter from '@lib/components/filters/CCheckboxFilter.vue'
import CSearchFilter from '@lib/components/filters/CSearchFilter.vue'
import CSelectFilter from '@lib/components/filters/CSelectFilter.vue'
import CDateRangeFilter from '@lib/components/filters/CDateRangeFilter.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import useMetadataFilters from '@lib/composables/useMetadataFilters'
import useThemeVars from '@lib/composables/useThemeVars'
import { SystemFilter } from '@lib/types/sectionPopularity'
import type { MetadataTemplateFieldWithComponentName } from '@lib/types/filter'

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    engagingIds?: string[]
    metadataFilterValues?: Record<string, any>
    popularIds?: string[]
    recommendedIds?: string[]
    showLatestFilter?: boolean
  }>(),
  {
    engagingIds: () => [],
    metadataFilterValues: () => ({}),
    popularIds: () => [],
    recommendedIds: () => [],
    showLatestFilter: false,
  },
)

const emit = defineEmits<{
  'update:metadata-filter-values': [mf: Record<string, any>]
}>()

const { t } = useI18n()
const { getFilterOptions, getComponentNameByFilterType } = useMetadataFilters(t)
const themeVars = useThemeVars()

const componentMap = {
  CCheckboxFilter,
  CSearchFilter,
  CSelectFilter,
  CDateRangeFilter,
} as Record<string, any>
const template = ref<Required<PaginatedMetadataTemplateList>['results'][number]>()

const systemFilters = computed(() => {
  const filters: MetadataTemplateFieldWithComponentName[] = []

  if (props.popularIds?.length)
    filters.push({
      componentName: 'CCheckboxFilter',
      label: t('canvasUI.components.canvases.CCanvasMetadataFilters.popular'),
      name: SystemFilter.POPULAR,
      type: MetadataTemplateFieldTypeEnum.CHECKBOX,
    })

  if (props.engagingIds?.length)
    filters.push({
      componentName: 'CCheckboxFilter',
      label: t('canvasUI.components.canvases.CCanvasMetadataFilters.engaging'),
      name: SystemFilter.ENGAGING,
      type: MetadataTemplateFieldTypeEnum.CHECKBOX,
    })

  if (props.recommendedIds?.length)
    filters.push({
      componentName: 'CCheckboxFilter',
      label: t('canvasUI.components.canvases.CCanvasMetadataFilters.recommended'),
      name: SystemFilter.RECOMMENDED,
      type: MetadataTemplateFieldTypeEnum.CHECKBOX,
    })

  if (props.showLatestFilter)
    filters.push({
      componentName: 'CCheckboxFilter',
      label: t('canvasUI.components.canvases.CCanvasMetadataFilters.latest'),
      name: SystemFilter.LATEST,
      type: MetadataTemplateFieldTypeEnum.CHECKBOX,
    })

  return filters
})

const fields = computed(() => {
  const sections = template.value?.sections ?? []

  return sections.flatMap((s) => s.fields).filter((f) => f.is_filterable)
})

const filters = computed(() => {
  const metadataFilters = fields.value.reduce((acc, { label, name, type, help_text, options }) => {
    const componentName = getComponentNameByFilterType(type)

    return [
      ...acc,
      {
        label,
        name,
        type,
        help_text,
        componentName,
        options: getFilterOptions(type, options),
      },
    ]
  }, [] as MetadataTemplateFieldWithComponentName[])

  return [...metadataFilters, ...systemFilters.value]
})

const hasActiveFilters = computed(() =>
  Object.values(props.metadataFilterValues).some((v) => {
    if (Array.isArray(v)) {
      return v.length > 0 // Check if the array is not empty
    }

    return v !== '' && v !== null && v !== false // Check falsy values for non-array values
  }),
)

const metadataFetcher = inject<
  (params?: GetInstanceMetadataTemplatesPayload) => Promise<PaginatedMetadataTemplateList>
>('instanceMetadataTemplatesFetcher')

function onUpdateModelValue(f: MetadataTemplateField, value: any) {
  return emit(
    'update:metadata-filter-values',
    omitBy({ ...props.metadataFilterValues, [f.name]: value }, (v) => v === '' || v === null),
  )
}

onMounted(async () => {
  try {
    const response = await metadataFetcher?.()
    const results = response?.results ?? ([] as PaginatedMetadataTemplateList['results'])

    if (!results?.length) {
      console.warn('[CCanvasMetadataFilters]: No metadata templates found. Bailing...')
      return
    }

    const [t] = results
    template.value = t
  } catch (e) {
    console.error('[CCanvasMetadataFilters]: Error fetching metadata templates', e)
  }
})
</script>
